/** Role helpers over the Cognito ID token. */
import type { User } from "oidc-client-ts";

/** Cognito groups, mirroring the workpaper Cover Sheet sign-off rows.
 *  Defined here rather than in config so that importing auth helpers does not
 *  drag in runtime configuration validation. */
export const ROLES = {
  admin: "Admin",
  preparer: "Preparer",
  reviewer: "Reviewer",
  director: "DirectorApproval",
} as const;

/**
 * Groups from the ID token.
 *
 * Cognito emits `cognito:groups` as a JSON array in the token itself; the
 * bracketed-string shapes API Gateway produces do not occur here, but the
 * array is still defensively normalised because an absent claim is normal for
 * a user an admin has not assigned a role to yet.
 */
export function groupsOf(user: User | null | undefined): string[] {
  const raw = user?.profile?.["cognito:groups"];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string" && raw.length > 0) return [raw];
  return [];
}

export function hasRole(user: User | null | undefined, role: string): boolean {
  return groupsOf(user).includes(role);
}

export function isAdmin(user: User | null | undefined): boolean {
  return hasRole(user, ROLES.admin);
}

/** A user with no group cannot do anything, and should be told why. */
export function hasAnyRole(user: User | null | undefined): boolean {
  return groupsOf(user).length > 0;
}

export function displayName(user: User | null | undefined): string {
  const profile = user?.profile;
  return String(profile?.email ?? profile?.["cognito:username"] ?? "Signed in");
}

// ---------------------------------------------------------------------------
// Resuming a session after a refresh
// ---------------------------------------------------------------------------

/**
 * Marks that a promptless sign-in has already been tried in this tab.
 *
 * A flag, not a token: the point of keeping tokens in memory is that nothing
 * readable by an injected script survives the page, and "we already asked"
 * is not worth reading.
 */
const ATTEMPTED = "casa.resume-attempted";

/** Session storage, or nothing, in a browser that refuses it. */
function flags(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function resumeAttempted(): boolean {
  return flags()?.getItem(ATTEMPTED) === "1";
}

export function markResumeAttempted(): void {
  flags()?.setItem(ATTEMPTED, "1");
}

export function clearResumeAttempt(): void {
  flags()?.removeItem(ATTEMPTED);
}

// ---------------------------------------------------------------------------
// Arriving from Microsoft's My Apps
// ---------------------------------------------------------------------------

/** The Cognito identity provider's name, as Terraform creates it. */
export const MICROSOFT_PROVIDER = "Microsoft";

/**
 * Whether this visit came from the BAS tile in My Apps, which links to
 * `?signin=microsoft`.
 *
 * Someone who clicks that tile is already signed in to Microsoft, so the
 * sign-in page and its "Sign in with Microsoft" button are two clicks that
 * decide nothing. Asking Cognito for that provider directly skips both.
 */
export function arrivedFromMyApps(search: string = window.location.search): boolean {
  return new URLSearchParams(search).get("signin") === "microsoft";
}

/**
 * Whether Cognito told us there is no session to resume.
 *
 * `prompt=none` asks for tokens without showing anything. When no hosted
 * session exists Cognito answers with an OAuth error rather than a login
 * form, and that is an expected outcome -- it means "ask them to sign in",
 * not "something broke". Anything else is a real failure worth showing.
 */
export function isNoSessionError(error: Error | null | undefined): boolean {
  if (!error) return false;
  // oidc-client-ts puts the OAuth code ("login_required") in `error` and the
  // human description ("Login required") in `message`, so read both.
  const code = (error as { error?: unknown }).error;
  const text = `${error.name} ${error.message} ${typeof code === "string" ? code : ""}`
    .toLowerCase()
    .replace(/ required\b/g, "_required");
  return (
    text.includes("login_required") ||
    text.includes("interaction_required") ||
    text.includes("consent_required") ||
    text.includes("account_selection_required")
  );
}

// ---------------------------------------------------------------------------
// Coming back to the page you were on
// ---------------------------------------------------------------------------

/** Where Cognito sends the browser back to; never a place to return to. */
export const CALLBACK_PATH = "/auth/callback";

const RETURN_TO = "casa.return-to";

/** What the OIDC `state` carries through a sign-in redirect. */
export interface SigninState {
  returnTo: string;
}

/**
 * Only a path on this origin. `//evil.example` is a path to a browser's URL
 * parser but a different host to `history.replaceState`'s caller, and the
 * callback itself is a dead end.
 */
function safeReturnTo(value: unknown): string | null {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return null;
  }
  if (value.includes("\\")) return null;
  if (value === CALLBACK_PATH || value.startsWith(`${CALLBACK_PATH}?`)) return null;
  return value;
}

/**
 * Remember where this visit started, and return it for the OIDC `state`.
 *
 * Every page load starts signed out, because tokens live in memory. That
 * includes the Xero callback, which lands on `/?xero=connected&tenant=…` or
 * `/?xero=error&reason=…`: the promptless sign-in that follows used to come
 * back to `/auth/callback`, have its query stripped, and leave the Clients
 * page nothing to read -- a failed connection looked exactly like no attempt.
 *
 * Carried twice: in the OIDC `state`, which comes back with the user, and in
 * session storage for the case where the promptless attempt fails and the
 * person then presses Sign in from the callback URL, where the current
 * location is no longer the one worth keeping.
 *
 * `signin=microsoft` is dropped: it has done its job once the redirect starts,
 * and keeping it would send every later refresh straight to Microsoft.
 */
export function rememberReturnTo(location: Location = window.location): SigninState {
  const stored = safeReturnTo(flags()?.getItem(RETURN_TO));
  if (location.pathname === CALLBACK_PATH) {
    return { returnTo: stored ?? "/" };
  }
  const params = new URLSearchParams(location.search);
  params.delete("signin");
  const query = params.toString();
  const here =
    safeReturnTo(`${location.pathname}${query ? `?${query}` : ""}${location.hash}`) ?? "/";
  flags()?.setItem(RETURN_TO, here);
  return { returnTo: here };
}

/**
 * Where to put the address bar after a sign-in completes, consuming the
 * stored copy. The `state` wins; storage covers a state that did not survive.
 */
export function takeReturnTo(state: unknown): string {
  const stored = flags()?.getItem(RETURN_TO);
  flags()?.removeItem(RETURN_TO);
  const fromState =
    state && typeof state === "object" ? (state as Partial<SigninState>).returnTo : undefined;
  return safeReturnTo(fromState) ?? safeReturnTo(stored) ?? "/";
}

/**
 * The `onSigninCallback`: strip the OIDC query so a refresh does not replay
 * the authorization code (Cognito rejects it the second time), and put back
 * the address the sign-in started from. Runs before the app renders signed
 * in, so the page sees the restored query on its first render.
 */
export function restoreAfterSignin(user?: { state?: unknown } | null | void): void {
  window.history.replaceState({}, document.title, takeReturnTo(user?.state));
}
